import { Button } from '@/components/ui/button';
import { useTelegram } from '@/hooks/useTelegram';
import { cn } from '@/lib/utils';
import { ReactNode, useEffect } from 'react';

// TelegramMainButton component
interface TelegramMainButtonProps {
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'default' | 'destructive' | 'secondary';
  className?: string;
}

export const TelegramMainButton = ({
  text,
  onClick,
  disabled = false,
  loading = false,
  variant = 'default',
  className
}: TelegramMainButtonProps) => {
  const { webApp, isInTelegram, hapticFeedback } = useTelegram();

  useEffect(() => {
    if (!isInTelegram || !webApp) return;

    const mainButton = webApp.MainButton;
    mainButton.setText(text);

    if (disabled || loading) {
      mainButton.disable();
    } else {
      mainButton.enable();
    }

    if (loading) {
      mainButton.showProgress();
    } else {
      mainButton.hideProgress();
    }

    const handleClick = () => {
      hapticFeedback.selection();
      onClick?.();
    };

    if (onClick) {
      mainButton.onClick(handleClick);
      mainButton.show();
    } else {
      mainButton.hide();
    }

    return () => {
      mainButton.offClick(handleClick);
      mainButton.hide();
    };
  }, [webApp, isInTelegram, text, onClick, disabled, loading, hapticFeedback]);

  // Fallback button for non-Telegram environments
  if (!isInTelegram) {
    return (
      <Button
        onClick={onClick}
        disabled={disabled || loading}
        variant={variant}
        className={cn("w-full", className)}
      >
        {loading ? 'Loading...' : text}
      </Button>
    );
  }

  return null; // In Telegram, the native MainButton is used
};

// TelegramBackButton component
interface TelegramBackButtonProps {
  onBack?: () => void;
  show?: boolean;
}

export const TelegramBackButton = ({
  onBack,
  show = true
}: TelegramBackButtonProps) => {
  const { webApp, isInTelegram, hapticFeedback } = useTelegram();

  useEffect(() => {
    if (!isInTelegram || !webApp) return;

    const backButton = webApp.BackButton;

    const handleBack = () => {
      hapticFeedback.light();
      onBack?.();
    };

    if (show && onBack) {
      backButton.onClick(handleBack);
      backButton.show();
    } else {
      backButton.hide();
    }

    return () => {
      backButton.offClick(handleBack);
      backButton.hide();
    };
  }, [webApp, isInTelegram, onBack, show, hapticFeedback]);

  return null; // Native BackButton in Telegram
};

// TelegramThemeProvider - applies Telegram theme to the app
interface TelegramThemeProviderProps {
  children: ReactNode;
}

export const TelegramThemeProvider = ({ children }: TelegramThemeProviderProps) => {
  const { theme, isInTelegram, isInitialized } = useTelegram();

  useEffect(() => {
    if (!isInTelegram || !isInitialized) return;

    // Apply Telegram theme colors to CSS custom properties
    const root = document.documentElement;

    // Convert hex to HSL for Tailwind compatibility
    const hexToHsl = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0;
      const l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }

      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    try {
      // Check if user has explicitly set theme preference (not "system")
      const userThemePreference = localStorage.getItem('supabank-theme');
      const hasExplicitTheme = userThemePreference && userThemePreference !== 'system';

      // Always set Telegram-specific CSS variables (for Telegram UI components)
      if (theme.colors.bg) {
        root.style.setProperty('--tg-bg', hexToHsl(theme.colors.bg));
      }

      if (theme.colors.text) {
        root.style.setProperty('--tg-text', hexToHsl(theme.colors.text));
      }

      if (theme.colors.button) {
        root.style.setProperty('--tg-button', hexToHsl(theme.colors.button));
      }

      if (theme.colors.secondaryBg) {
        root.style.setProperty('--tg-secondary-bg', hexToHsl(theme.colors.secondaryBg));
      }

      // Only override main theme CSS variables if user hasn't explicitly chosen a theme
      // This respects user's explicit dark/light mode choice
      if (!hasExplicitTheme) {
        // User is on "system" theme, so we can apply Telegram theme
        if (theme.colors.bg) {
          root.style.setProperty('--background', hexToHsl(theme.colors.bg));
        }

        if (theme.colors.text) {
          root.style.setProperty('--foreground', hexToHsl(theme.colors.text));
        }

        if (theme.colors.button) {
          root.style.setProperty('--primary', hexToHsl(theme.colors.button));
        }

        if (theme.colors.secondaryBg) {
          root.style.setProperty('--muted', hexToHsl(theme.colors.secondaryBg));
        }

        // Set dark/light mode class based on Telegram theme
        if (theme.isDark) {
          root.classList.add('dark');
          root.classList.remove('light');
        } else {
          root.classList.add('light');
          root.classList.remove('dark');
        }
      }
      // If user has explicit theme preference, keep it (don't override)
      // The .dark/.light class is already managed by next-themes ThemeProvider
    } catch (error) {
      console.warn('Failed to apply Telegram theme:', error);
    }
  }, [theme, isInTelegram, isInitialized]);

  return <>{children}</>;
};

// TelegramHaptic - helper component for haptic feedback on interactions
interface TelegramHapticProps {
  children: ReactNode;
  type?: 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'error' | 'warning';
  disabled?: boolean;
  className?: string;
}

export const TelegramHaptic = ({
  children,
  type = 'selection',
  disabled = false,
  className
}: TelegramHapticProps) => {
  const { hapticFeedback, isInTelegram } = useTelegram();

  const handleInteraction = () => {
    if (!isInTelegram || disabled) return;

    switch (type) {
      case 'light':
        hapticFeedback.light();
        break;
      case 'medium':
        hapticFeedback.medium();
        break;
      case 'heavy':
        hapticFeedback.heavy();
        break;
      case 'success':
        hapticFeedback.success();
        break;
      case 'error':
        hapticFeedback.error();
        break;
      case 'warning':
        hapticFeedback.warning();
        break;
      case 'selection':
      default:
        hapticFeedback.selection();
        break;
    }
  };

  return (
    <div
      className={className}
      onClick={handleInteraction}
      onTouchStart={handleInteraction}
    >
      {children}
    </div>
  );
};